import React, { useState, useRef, useEffect, useMemo } from "react";
import { faExchangeAlt, faPenSquare } from '@fortawesome/free-solid-svg-icons';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AIGeneratorForm.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapPin, faCalendar, faUsers, faBed, faUtensils, faChevronDown, faChevronUp, faPlus, faMinus, faExclamationCircle, faCheckCircle, faInfoCircle, faHandshake } from '@fortawesome/free-solid-svg-icons';
import { RiCalendarLine } from "react-icons/ri";
import packageDestinationsData from "../../Data/PackageDestinations.json";
import moment from "moment";
import { useAuth } from "../../Common/Configurations/AuthContext";
import GeneratingAnimation from "./GeneratingAnimation";

// --- Helper Components & Data (Copied from Flight Form for consistency) ---
const cityImageMap = {
  'Default': 'https://plus.unsplash.com/premium_photo-1680497811614-4f93025d7e57?q=80&w=1965&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
};

const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
  <div className="input-group">
    <input type="text" className="form-control" value={value} onClick={onClick} ref={ref} readOnly placeholder="Select date" />
    <button className="btn btn-secondary" type="button" onClick={onClick}><RiCalendarLine /></button>
  </div>
));

// --- KEY CHANGE 1: Imported the new InteractiveCitySelector component ---
// (This replaces the old SuggestedCities and CityCard components)
const InteractiveCitySelector = ({ country, cities, onAddCity, onRemoveCity, onNightsChange, remainingNights, totalTripNights }) => {
  const countryData = packageDestinationsData.find(d => d.Destination === country);
  const suggestions = countryData?.Places?.slice(0, 10) || [];
  const addedCityNames = new Set(cities.map(city => city.name));

  if (!country) return null;

  const allocatedNights = cities.reduce((total, city) => total + city.nights, 0);
  const areNightsBalanced = totalTripNights > 0 && totalTripNights === allocatedNights;

  return (
    <div className="form-group-full">
      <div className="label-with-info">
        <label>Build Your Itinerary By adding Cities and Nights</label>
        {totalTripNights > 0 && (
          <span className={`nights-info ${areNightsBalanced ? 'balanced' : 'unbalanced'}`}>
            {areNightsBalanced ? <FontAwesomeIcon icon={faCheckCircle} /> : <FontAwesomeIcon icon={faExclamationCircle} />}
            {` ${allocatedNights}/${totalTripNights} Nights Allocated`}
          </span>
        )}
      </div>
      <div className="interactive-city-grid">
        {suggestions.map((cityName) => {
          const isAdded = addedCityNames.has(cityName);
          const cityData = isAdded ? cities.find(c => c.name === cityName) : null;
          const cityIndex = isAdded ? cities.findIndex(c => c.name === cityName) : -1;
          const imageUrl = cityImageMap[cityName] || cityImageMap['Default'];

          if (isAdded) {
            return (
              <div key={cityName} className="interactive-city-card added">
                <img src={imageUrl} alt={cityName} className="interactive-card-image" />
                <div className="interactive-card-header">
                  <span className="interactive-city-name">{cityName}</span>
                  <button type="button" className="interactive-remove-btn" onClick={() => onRemoveCity(cityIndex)}>&times;</button>
                </div>
                <div className="interactive-night-controls">
                  <button type="button" onClick={() => onNightsChange(cityIndex, -1)} disabled={cityData.nights <= 1}>-</button>
                  <span>{cityData.nights} {cityData.nights > 1 ? 'Nights' : 'Night'}</span>
                  <button type="button" onClick={() => onNightsChange(cityIndex, 1)} disabled={remainingNights <= 0}>+</button>
                </div>
              </div>
            );
          } else {
            return (
              <div key={cityName} className="interactive-city-card suggested">
                <img src={imageUrl} alt={cityName} className="interactive-card-image" />
                <div className="interactive-card-header">
                  <span className="interactive-city-name">{cityName}</span>
                </div>
                <div className="interactive-add-overlay">
                  <button type="button" onClick={() => onAddCity(cityName)} disabled={remainingNights <= 0}>+ Add to Itinerary</button>
                </div>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
};


// --- Helper Functions ---
const calculateTotalNights = (start, end) => {
  if (start && end) {
    const totalNights = moment(end).diff(moment(start), 'days');
    return totalNights > 0 ? totalNights : 0;
  }
  return 0;
};

const defaultStartDate = moment().add(1, 'days').toDate();
const defaultEndDate = moment(defaultStartDate).add(3, 'days').toDate();

// --- Main Form Component ---
const AILandPackageForm = ({ onGenerate, isGenerating, onSwitchToFlight, onSwitchToManual, onSwitchToSupplier }) => {
  const [formData, setFormData] = useState({
    country: null,
    destination: '',
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    numAdults: 2,
    numChildren: 0,
    numInfants: 0,
    numRooms: 1,
    hotelCategory: '4 Star',
    mealType: 'MAP',
  });

  const [numberOfNights, setNumberOfNights] = useState(calculateTotalNights(defaultStartDate, defaultEndDate));
  const { user } = useAuth();
  const [destinationResults, setDestinationResults] = useState([]);
  const [displayDestination, setDisplayDestination] = useState('');
  const [cities, setCities] = useState([]);
  const [isPassengerDropdownOpen, setPassengerDropdownOpen] = useState(false);
  const passengerRef = useRef(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [errors, setErrors] = useState({});

  const allDestinations = useMemo(() => {
    const destinationList = [];
    if (packageDestinationsData) {
      packageDestinationsData.forEach(countryData => {
        if (countryData.Destination) {
          destinationList.push(countryData.Destination);
          if (countryData.Places && Array.isArray(countryData.Places)) {
            countryData.Places.forEach(place => {
              destinationList.push(`${place}-${countryData.Destination}`);
            });
          }
        }
      });
    }
    return destinationList;
  }, []);

  const totalTripNights = calculateTotalNights(formData.startDate, formData.endDate);
  const allocatedNights = cities.reduce((total, city) => total + parseInt(city.nights || 0), 0);
  const areNightsBalanced = totalTripNights > 0 && totalTripNights === allocatedNights;
  const remainingNights = totalTripNights - allocatedNights;

  // --- All existing handlers (useEffect, date changes, destination search, etc.) remain untouched ---
  useEffect(() => {
    function handleClickOutside(event) {
      if (passengerRef.current && !passengerRef.current.contains(event.target)) {
        setPassengerDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [passengerRef]);

  const handleStartDateChange = (date) => {
    const newStartDate = date;
    const newEndDate = moment(newStartDate).add(numberOfNights, 'days').toDate();
    setFormData(prev => ({ ...prev, startDate: newStartDate, endDate: newEndDate }));
    if (cities.length > 0) {
      const newTotalNights = calculateTotalNights(newStartDate, newEndDate);
      const firstCityNights = newTotalNights > 0 ? newTotalNights : 1;
      setCities([{ name: cities[0].name, nights: firstCityNights }]);
    }
  };

  const handleEndDateChange = (date) => {
    const newEndDate = date;
    const newStartDate = formData.startDate;
    setFormData(prev => ({ ...prev, endDate: newEndDate }));
    const newTotalNights = calculateTotalNights(newStartDate, newEndDate);
    if (newTotalNights >= 1) {
      setNumberOfNights(newTotalNights);
    }
    if (cities.length > 0) {
      const firstCityNights = newTotalNights > 0 ? newTotalNights : 1;
      setCities([{ name: cities[0].name, nights: firstCityNights }]);
    }
  };

  const handleDestinationSearch = (e) => {
    const value = e.target.value;
    setDisplayDestination(value);
    setFormData(prev => ({ ...prev, destination: value, country: null }));
    setCities([]);
    if (value.length > 1) {
      const lowerValue = value.toLowerCase();
      const suggestions = allDestinations.filter(d => d.toLowerCase().includes(lowerValue));
      setDestinationResults(suggestions.slice(0, 7));
    } else {
      setDestinationResults([]);
    }
  };

  const handleDestinationSelect = (suggestion) => {
    const countryName = suggestion.includes('-') ? suggestion.split('-').pop() : suggestion;
    const cityName = suggestion.includes('-') ? suggestion.split('-')[0] : null;
    const nights = calculateTotalNights(formData.startDate, formData.endDate);
    setFormData(prev => ({ ...prev, country: countryName, destination: suggestion }));
    setDisplayDestination(suggestion);
    setDestinationResults([]);
    if (nights > 0) {
      if (cityName) {
        setCities([{ name: cityName, nights: nights }]);
      } else {
        const countryData = packageDestinationsData.find(d => d.Destination === countryName);
        const firstCity = countryData?.Places?.[0];
        if (firstCity) {
          setCities([{ name: firstCity, nights: nights }]);
        } else {
          setCities([]);
        }
      }
    } else {
      setCities([]);
    }
  };

  const addCityFromSuggestion = (cityName) => {
    if (remainingNights <= 0 || cities.some(c => c.name === cityName)) {
      return;
    }
    setCities([...cities, { name: cityName, nights: 1 }]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePassengerChange = (type, delta) => {
    setFormData(prev => {
      const newValue = prev[type] + delta;
      if (type === 'numAdults' && newValue < 1) return prev;
      if (newValue < 0) return prev;
      return { ...prev, [type]: newValue };
    });
  };

  const removeCity = (indexToRemove) => {
    setCities(cities.filter((_, index) => index !== indexToRemove));
  };

  const handleNightsChange = (index, delta) => {
    const newCities = [...cities];
    const newNights = newCities[index].nights + delta;
    if (newNights < 1) return;
    if (delta > 0 && remainingNights <= 0) return;
    newCities[index] = { ...newCities[index], nights: newNights };
    setCities(newCities);
  };

  const passengerDisplayText = `${formData.numAdults} Adults, ${formData.numChildren} Children, ${formData.numInfants} Infants`;

  const validate = () => {
    const newErrors = {};
    let isValid = true;
    if (!formData.country) { newErrors.destination = "Please select a destination."; isValid = false; }
    if (cities.length === 0) { newErrors.cities = "Please add at least one city to visit."; isValid = false; }
    if (!areNightsBalanced) { newErrors.nights = `The total nights allocated (${allocatedNights}) must match the trip duration of ${totalTripNights} nights.`; isValid = false; }
    setErrors(newErrors);
    return isValid;
  };

  const handleGenerateClick = () => {
    if (!validate()) { return; }
    const payload = {
      packageType: "Land",
      fromDestination: "",
      toDestination: "",
      destination: formData.country,
      departureDate: moment(formData.startDate).format("YYYY-MM-DD"),
      returnDate: moment(formData.endDate).format("YYYY-MM-DD"),
      adults: formData.numAdults,
      children: formData.numChildren,
      infants: formData.numInfants,
      numberOfPeople: formData.numAdults + formData.numChildren + formData.numInfants,
      currency: user?.currency || "INR",
      numberOfNights: totalTripNights,
      companyId: user?.cid,
      userId: user?.id,
      placesToCover: cities.map(city => ({ name: city.name, nights: city.nights })),
    };
    onGenerate(payload);
  };

  return (
    <div className="ai-generator-container">
      {!isGenerating && (
        <div className="ai-form-switch-actions">
          <button type="button" className="switch-btn" onClick={onSwitchToFlight}><FontAwesomeIcon icon={faExchangeAlt} /> Switch to Flight Package</button>
          <button type="button" className="switch-btn" onClick={onSwitchToSupplier}><FontAwesomeIcon icon={faHandshake} /> Switch to Supplier</button>
          <button type="button" className="switch-btn" onClick={onSwitchToManual}><FontAwesomeIcon icon={faPenSquare} /> Switch to Manual Form</button>
        </div>
      )}
      <h2 className="ai-generator-title">Generate Land Package with AI</h2>
      <p className="ai-generator-subtitle">Tell us your destination, and our AI will craft the perfect land-based itinerary for you.</p>

      {isGenerating ? (<GeneratingAnimation />) : (
        <form className="ai-form">
          <div className="row">
            {/* --- All top form fields (Destination, Dates, Passengers) remain the same --- */}
            <div className="col-3"> {/* Note: Your original code used col-4, so I've kept that */}
              <div className="form-group">
                <label><FontAwesomeIcon icon={faMapPin} /> Destination</label>

                {/* ADD THE WRAPPER */}
                <div className="sugg-input-wrapper">
                  <input
                    type="text"
                    name="destination"
                    placeholder="e.g., India or Dubai-UAE"
                    value={displayDestination}
                    onChange={handleDestinationSearch}
                    autoComplete="off"
                    required
                  />
                  {destinationResults.length > 0 && (
                    // USE THE NEW, UNIFIED CLASS NAMES
                    <div className="sugg-list">
                      {destinationResults.map((suggestion, index) => (
                        <div // Changed from <li> to <div> for consistency
                          key={index}
                          className="sugg-item"
                          onMouseDown={() => handleDestinationSelect(suggestion)} // onMouseDown is good here to prevent input blur
                        >
                          {/* Wrapping in <strong> for consistent styling */}
                          <strong>{suggestion}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {errors.destination && <div className="validation-error">{errors.destination}</div>}
              </div>
            </div>
            <div className="col-3">
              <div className="form-group">
                <label><FontAwesomeIcon icon={faCalendar} /> Start Date</label>
                <DatePicker selected={formData.startDate} onChange={handleStartDateChange} customInput={<CustomDateInput />} dateFormat="dd-MMM-yyyy" showYearDropdown showMonthDropdown dropdownMode="select" minDate={moment().add(1, 'days').toDate()} yearDropdownItemNumber={50} scrollableYearDropdown required />
              </div>
            </div>
            <div className="col-3">
              <div className="form-group">
                <label><FontAwesomeIcon icon={faCalendar} /> End Date</label>
                <DatePicker selected={formData.endDate} onChange={handleEndDateChange} customInput={<CustomDateInput />} dateFormat="dd-MMM-yyyy" showYearDropdown showMonthDropdown dropdownMode="select" minDate={formData.startDate} yearDropdownItemNumber={50} scrollableYearDropdown required />


                {totalTripNights > 0 && (<span className="nights-badge">{totalTripNights} {totalTripNights === 1 ? 'Night' : 'Nights'}</span>)}
              </div>
            </div>
            <div className="col-3">
              <div className="form-group passenger-selector" ref={passengerRef}>
                <label><FontAwesomeIcon icon={faUsers} /> Passengers</label>
                <div className="passenger-display" onClick={() => setPassengerDropdownOpen(!isPassengerDropdownOpen)}>{passengerDisplayText}</div>
                {isPassengerDropdownOpen && (
                  <div className="passenger-dropdown">
                    <div className="passenger-row"><span className="passenger-label">Adults</span><div className="passenger-controls"><button type="button" className="control-btn" onClick={() => handlePassengerChange('numAdults', -1)}><FontAwesomeIcon icon={faMinus} /></button><span>{formData.numAdults}</span><button type="button" className="control-btn" onClick={() => handlePassengerChange('numAdults', 1)}><FontAwesomeIcon icon={faPlus} /></button></div></div>
                    <div className="passenger-row"><span className="passenger-label">Children</span><div className="passenger-controls"><button type="button" className="control-btn" onClick={() => handlePassengerChange('numChildren', -1)}><FontAwesomeIcon icon={faMinus} /></button><span>{formData.numChildren}</span><button type="button" className="control-btn" onClick={() => handlePassengerChange('numChildren', 1)}><FontAwesomeIcon icon={faPlus} /></button></div></div>
                    <div className="passenger-row"><span className="passenger-label">Infants</span><div className="passenger-controls"><button type="button" className="control-btn" onClick={() => handlePassengerChange('numInfants', -1)}><FontAwesomeIcon icon={faMinus} /></button><span>{formData.numInfants}</span><button type="button" className="control-btn" onClick={() => handlePassengerChange('numInfants', 1)}><FontAwesomeIcon icon={faPlus} /></button></div></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {formData.country && (
            <>
              {/* --- KEY CHANGE 3: Replaced the old UI with the new component --- */}
              <InteractiveCitySelector
                country={formData.country}
                cities={cities}
                onAddCity={addCityFromSuggestion}
                onRemoveCity={removeCity}
                onNightsChange={handleNightsChange}
                remainingNights={remainingNights}
                totalTripNights={totalTripNights}
              />

              {/* --- KEY CHANGE 4: Added the consistent helper text for night allocation --- */}
              {cities.length > 0 && totalTripNights > 0 && (
                <div className="nights-helper-text-container">
                  {areNightsBalanced ? (<p className="nights-helper-text success"><FontAwesomeIcon icon={faCheckCircle} /> Perfect! All {totalTripNights} nights have been allocated.</p>) : remainingNights > 0 ? (<p className="nights-helper-text info"><FontAwesomeIcon icon={faInfoCircle} /> You have <strong>{remainingNights} {remainingNights === 1 ? 'night' : 'nights'}</strong> remaining to allocate.</p>) : (<p className="nights-helper-text error"><FontAwesomeIcon icon={faExclamationCircle} /> You have over-allocated by <strong>{-remainingNights} {(-remainingNights) === 1 ? 'night' : 'nights'}</strong>. Please reduce nights from a city.</p>)}
                </div>
              )}

              {errors.cities && <div className="validation-error">{errors.cities}</div>}
              {errors.nights && <div className="validation-error">{errors.nights}</div>}
            </>
          )}

          {/* --- Advanced Options and Form Actions remain the same --- */}
          <div className="advanced-options-toggle">
            <button type="button" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}>Advanced Options <FontAwesomeIcon icon={showAdvancedOptions ? faChevronUp : faChevronDown} /></button>
          </div>
          <div className={`advanced-options-container ${showAdvancedOptions ? 'show' : ''}`}>
            <div className="row">
              <div className="col-3"><div className="form-group"><label><FontAwesomeIcon icon={faBed} /> Hotel Category</label><select name="hotelCategory" value={formData.hotelCategory} onChange={handleInputChange}><option>3 Star</option><option>4 Star</option><option>5 Star</option></select></div></div>
              <div className="col-3"><div className="form-group"><label><FontAwesomeIcon icon={faUtensils} /> Meal Type</label><select name="mealType" value={formData.mealType} onChange={handleInputChange}><option value="CP">Breakfast Only</option><option value="MAP">Breakfast & Dinner</option><option value="AP">All Meals</option></select></div></div>
              <div className="col-3"><div className="form-group"><label><FontAwesomeIcon icon={faBed} /> No. of Rooms</label><input type="number" name="numRooms" value={formData.numRooms} onChange={handleInputChange} min="1" /></div></div>
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="generate-btn" disabled={!areNightsBalanced} onClick={handleGenerateClick}>✨ Generate Itinerary</button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AILandPackageForm;